import { ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { Button, Loading } from 'react-daisyui';
import { useTranslation } from 'react-i18next';
import { useAnalytics } from 'src/hooks/analytics/useAnalytics.hook';
import { useUserAvailability } from 'src/hooks/availability/useUserAvailability.hook';

/**
 * Persistent banner shown on every private route while the signed-in user is
 * Inactivo (RET-1350). The avatar pill alone doesn't cover the worst failure
 * mode — a seller who flipped to Inactivo and forgot for days while leads
 * skipped them in auto-assignment — so this stays visible until the state is
 * flipped back, with the one-click fix inline.
 *
 * Not dismissible on purpose: dismissing would recreate the silent-inactive
 * week the ticket exists to prevent, and "Volver a activo" already acts as
 * the close affordance for anyone who didn't mean to be inactive.
 *
 * Mounted once in `PrivateLayout` instead of per-page so it survives
 * navigation to any surface.
 */
export default function InactiveUserBanner() {
  // `ready` gates on the namespace fetch (useSuspense is off app-wide), so a
  // cold cache can never paint raw keys like "title" in an always-on banner.
  const { t, ready } = useTranslation('common/inactiveUserBanner');
  const { trackEvent } = useAnalytics();
  const { isActive, isLoaded, isUpdating, setAvailability } =
    useUserAvailability();

  if (!ready || !isLoaded || isActive) return null;

  function onBackToActive() {
    trackEvent('front.inactive_user_banner.button.click_back_to_active');
    setAvailability(true);
  }

  return (
    <div
      data-testid="inactive-user-banner"
      // role="status" so assistive tech announces the banner when it mounts
      // (same pattern as SyncProgressToast).
      role="status"
      aria-live="polite"
      // my-4 (CPO pass 2026-08-20): breathing room against the navbar above
      // and the page heading below — the banner sat flush against both.
      className="flex items-center justify-between gap-3 w-full my-4 text-sm bg-warning/10 border-l-4 border-warning rounded-r-md px-4 py-2"
    >
      <div className="flex items-start gap-3">
        <ExclamationTriangleIcon className="w-5 h-5 shrink-0 mt-0.5 text-warning" />
        <div>
          <p className="font-semibold">{t('title')}</p>
          <p className="text-base-content/70">{t('description')}</p>
        </div>
      </div>
      <Button
        size="sm"
        color="primary"
        onClick={onBackToActive}
        disabled={isUpdating}
        className="shrink-0"
      >
        {isUpdating ? <Loading size="sm" /> : null}
        {t('action')}
      </Button>
    </div>
  );
}
